import { useEffect, useState, FormEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { GOAL_PRIORITIES, type Goal, type GoalPriority } from '../../api/types';

export interface GoalDraft {
  title: string;
  description: string | null;
  priority: GoalPriority;
  /** YYYY-MM-DD, or null for no deadline. */
  deadline: string | null;
}

interface Props {
  open: boolean;
  /** The goal being edited; omit to create a new one. */
  goal?: Goal;
  onClose: () => void;
  onSubmit: (draft: GoalDraft) => Promise<void>;
}

/**
 * Create / edit form for a goal's title, priority, optional deadline (the
 * browser's own date picker) and description. Status
 * changes (done, dropped, reopen) live in the goal card's menu instead.
 */
export function GoalDialog({ open, goal, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<GoalPriority>('medium');
  const [deadline, setDeadline] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(goal?.title ?? '');
    setDescription(goal?.description ?? '');
    setPriority(goal?.priority ?? 'medium');
    setDeadline(goal?.deadline ?? '');
    setError('');
  }, [open, goal]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await onSubmit({
        title,
        description: description || null,
        priority,
        deadline: deadline || null,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('garageCar.goals.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {goal ? t('garageCar.goals.dialog.editTitle') : t('garageCar.goals.dialog.createTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label={t('garageCar.goals.dialog.title')}
              placeholder={t('garageCar.goals.dialog.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="goal-priority-label">{t('garageCar.goals.priorityLabel')}</InputLabel>
              <Select
                labelId="goal-priority-label"
                label={t('garageCar.goals.priorityLabel')}
                value={priority}
                onChange={(e) => setPriority(e.target.value as GoalPriority)}
              >
                {GOAL_PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {t(`garageCar.goals.priority.${p}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('garageCar.goals.deadline.label')}
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              helperText={t('garageCar.goals.deadline.hint')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label={t('garageCar.goals.dialog.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
